import React, { useEffect } from 'react';
import { Row, Col, Card, Statistic, Table, Typography, Alert, Spin, Tag, Button } from 'antd';
import { 
  TeamOutlined, 
  DollarOutlined, 
  UserAddOutlined,
  LineChartOutlined,
  DownloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import { useLeader } from '../../hooks/useLeader';

const { Title } = Typography;

const LeaderDashboard = () => {
  const { getDashboard, loading, error, data, downloadTeamReport } = useLeader();

  useEffect(() => {
     // Pour déboguer
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    await getDashboard();
  };

  const handleDownloadReport = async () => {
    try {
      const result = await downloadTeamReport({
        start_date: '2024-01-01',
        end_date: new Date().toISOString().split('T')[0]
      });
      
      if (result) {
        const url = window.URL.createObjectURL(new Blob([result]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `team_report_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  if (loading && !data) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  // CORRECTION : Utilisez 'title' au lieu de 'message'
  if (error) {
    console.error('Dashboard error:', error);
    return <Alert title="Erreur" description={error} type="error" showIcon />;
  }

  // CORRECTION : Gérez les données null/undefined
  const teamStats = data?.team_stats || {};
  const commissionStats = data?.commission_stats || {};
  
  // Vérifiez si les données sont chargées
  if (!data && !loading) {
    return (
      <Alert 
        title="Aucune donnée" 
        description="Les données du dashboard n'ont pas pu être chargées." 
        type="warning" 
        showIcon 
      />
    );
  }

  return (
    <div>
      <Title level={2}>Tableau de Bord Leader</Title>
      
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Membres d'Équipe"
              value={teamStats.total_team_members || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Volume Personnel Total"
              value={teamStats.total_personal_volume || 0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#52c41a' }}
              suffix="$"
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Volume Équipe Total"
              value={teamStats.total_team_volume || 0}
              prefix={<LineChartOutlined />}
              valueStyle={{ color: '#722ed1' }}
              suffix="$"
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Commissions Totales"
              value={commissionStats.total_earned || 0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#fa8c16' }}
              suffix="$"
            />
          </Card>
        </Col>
      </Row>
      
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} md={12}>
          <Card 
            title="Résumé de l'Équipe" 
            extra={
              <Button 
                type="link" 
                icon={<DownloadOutlined />} 
                onClick={handleDownloadReport}
              >
                Exporter
              </Button>
            }
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Équipe Directe"
                  value={teamStats.direct_team || 0}
                  prefix={<UserAddOutlined />}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Niveau 2"
                  value={teamStats.level2_team || 0}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Niveau 3+"
                  value={teamStats.deep_team || 0}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Invest. Aujourd'hui"
                  value={teamStats.today_investments || 0}
                  prefix={<DollarOutlined />}
                  suffix="$"
                />
              </Col>
            </Row>
          </Card>
        </Col>
        
        <Col xs={24} md={12}>
          <Card title="Statistiques des Commissions">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Gagnées"
                  value={commissionStats.total_earned || 0}
                  valueStyle={{ color: '#52c41a' }}
                  suffix="$"
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="En Attente"
                  value={commissionStats.total_pending || 0}
                  valueStyle={{ color: '#fa8c16' }}
                  suffix="$"
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Total"
                  value={commissionStats.total_commissions || 0}
                  suffix="transactions"
                />
              </Col>
              <Col span={12}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>Performance</div>
                  <Tag color="#52c41a" icon={<ArrowUpOutlined />}>
                    {((commissionStats.total_earned / (commissionStats.total_earned + commissionStats.total_pending)) * 100 || 0).toFixed(1)}%
                  </Tag>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
      
      {/* CORRECTION : Vérifiez si les données existent avant de les utiliser */}
      {data?.recent_team && data.recent_team.length > 0 && (
        <Card title="Derniers Membres d'Équipe" style={{ marginTop: 24 }}>
          <Table
            dataSource={data.recent_team}
            columns={[
              { 
                title: 'Membre', 
                dataIndex: 'username',
                render: (text, record) => (
                  <div>
                    <div style={{ fontWeight: 500 }}>{text || 'N/A'}</div>
                    <div style={{ fontSize: 12, color: '#999' }}>Niveau {record.level || 0}</div>
                  </div>
                )
              },
              { title: 'Email', dataIndex: 'email', render: email => email || 'N/A' },
              { 
                title: 'Volume Personnel', 
                dataIndex: 'personal_volume',
                render: (volume) => `$${parseFloat(volume || 0).toFixed(2)}`
              },
              { 
                title: 'Parrainages', 
                dataIndex: 'direct_referrals_count',
                align: 'center',
                render: count => count || 0
              },
              { 
                title: 'Date d\'adhésion', 
                dataIndex: 'created_at', 
                render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A'
              },
            ]}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </Card>
      )}
      
      {data?.team_investments && data.team_investments.length > 0 && (
        <Card title="Derniers Investissements de l'Équipe" style={{ marginTop: 24 }}>
          <Table
            dataSource={data.team_investments}
            columns={[
              { title: 'Membre', dataIndex: 'username', render: name => name || 'N/A' },
              { title: 'Plan', dataIndex: 'plan_name', render: plan => plan || 'N/A' },
              { 
                title: 'Montant', 
                dataIndex: 'amount',
                render: (amount) => `$${parseFloat(amount || 0).toFixed(2)}`
              },
              { 
                title: 'Niveau', 
                dataIndex: 'level',
                render: (level) => <Tag>Niveau {level || 0}</Tag>
              },
              { 
                title: 'Date de début', 
                dataIndex: 'start_date', 
                render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A'
              },
            ]}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </Card>
      )}
    </div>
  );
};

export default LeaderDashboard;